import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    HStack,
    Input,
    List,
    ListItem,
    Spinner,
    Text,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
    useSearchInsurance,
    type InsuranceSearchResult,
} from '../../hooks/useSearchInsurance';
import { SETTINGS_BORDER_COLOR } from './primitives';

export interface CarrierSearchFieldProps {
    onSelect: (company: InsuranceSearchResult) => void;
    autoFocus?: boolean;
    placeholder?: string;
    /**
     * Badge shown after a result's name, which also greys it out. Defaults to
     * the DRP flag the search endpoint returns; screens that group carriers
     * some other way say why a name is spoken for in their own terms.
     */
    statusFor?: (company: InsuranceSearchResult) => string | undefined;
    /** Results that carry a status but can still be picked. */
    isSelectable?: (company: InsuranceSearchResult) => boolean;
}

const defaultStatusFor = (company: InsuranceSearchResult) =>
    company.alreadyAdded ? 'Already added' : undefined;

/**
 * Carrier lookup: type three characters and pick from the insurers PartBay
 * knows. Carriers cannot be free-typed — a carrier row is keyed by insurer id —
 * and results already spoken for are flagged rather than hidden, so it is clear
 * why a familiar name cannot be added twice.
 */
const CarrierSearchField = ({
    onSelect,
    autoFocus = false,
    placeholder = 'Search insurance carriers...',
    statusFor = defaultStatusFor,
    isSelectable,
}: CarrierSearchFieldProps) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState('');
    const [showResults, setShowResults] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedTerm(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const { data, isLoading, error } = useSearchInsurance(debouncedTerm);
    const companies = data?.insuranceCompanies ?? [];

    return (
        <Box position="relative">
            <Input
                size="sm"
                h="36px"
                bg="white"
                autoFocus={autoFocus}
                value={searchTerm}
                placeholder={placeholder}
                onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowResults(e.target.value.length > 2);
                }}
                onFocus={() => searchTerm.length > 2 && setShowResults(true)}
                // Deferred so a click on a result lands before the list closes.
                onBlur={() => setTimeout(() => setShowResults(false), 150)}
            />
            {showResults && (
                <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    bg="white"
                    borderWidth="1px"
                    borderColor={SETTINGS_BORDER_COLOR}
                    borderRadius="md"
                    // Overlay chrome per Yaxle: hairline border, no drop shadow.
                    boxShadow="none"
                    zIndex={20}
                    maxH="240px"
                    overflowY="auto"
                >
                    {isLoading ? (
                        <Box p={4} textAlign="center">
                            <Spinner size="sm" />
                            <Text fontSize="sm" color="gray.500" mt={2}>
                                Searching...
                            </Text>
                        </Box>
                    ) : error ? (
                        <Box p={3}>
                            <Alert status="error" size="sm">
                                <AlertIcon />
                                <AlertDescription fontSize="sm">
                                    Error searching insurance companies
                                </AlertDescription>
                            </Alert>
                        </Box>
                    ) : companies.length > 0 ? (
                        <List spacing={0}>
                            {companies.map((company) => {
                                const status = statusFor(company);
                                const canSelect = isSelectable ? isSelectable(company) : true;
                                return (
                                    <ListItem
                                        key={company._id}
                                        px={4}
                                        py={2}
                                        cursor={canSelect ? 'pointer' : 'not-allowed'}
                                        _hover={canSelect ? { bg: 'gray.50' } : undefined}
                                        onClick={() => {
                                            if (!canSelect) return;
                                            onSelect(company);
                                            setSearchTerm('');
                                            setShowResults(false);
                                        }}
                                    >
                                        <HStack spacing={2}>
                                            <Text
                                                fontSize="sm"
                                                color={status ? 'gray.400' : 'gray.800'}
                                            >
                                                {company.name}
                                            </Text>
                                            {status && (
                                                <Badge colorScheme="gray" fontSize="2xs">
                                                    {status}
                                                </Badge>
                                            )}
                                        </HStack>
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : searchTerm.length > 5 ? (
                        <Box p={4}>
                            <Text fontSize="sm" color="gray.500">
                                No insurance companies found. If your insurer isn't listed, please
                                contact us.
                            </Text>
                        </Box>
                    ) : null}
                </Box>
            )}
        </Box>
    );
};

export default CarrierSearchField;
